import React, { useEffect, useState } from "react";
import { Form, Input, InputNumber, Select, DatePicker, Button, Switch, message, ConfigProvider } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { isDate1GreaterThanDate2 } from "../../utils/Common";
import useUserStore from "../../stores/store";
import ApiService from "../../utils/ApiService";
import CustomInputNumber from "../CustomInputNumber";
const { Option } = Select;

export default function CouponAddOrEdit({
  editData = {},
  isEdit = false,
  isRelaunch = false, // If true, resets dates/status but keeps other data
}) {
  const [form] = Form.useForm();
  const discountType = Form.useWatch("discount_type", form) ?? "PERCENT";
  const [loading, setLoading] = useState(false);
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const navigate = useNavigate();
  const { restaurantId, user, showNotification, setNetworkCall } = useUserStore();
  const [valueSharePercentage, setValueSharePercentage] = useState(0);
  const [discountValue, setDiscountValue] = useState(0);
  const [minOrderAmount, setMinOrderAmount] = useState(0);
  const [maxDiscountAmount, setMaxDiscountAmount] = useState(0);

  // Initial Data Loading
  useEffect(() => {
    if (isEdit && Boolean(editData) && Object.keys(editData).length > 0) {
      let initialValues = {};

      initialValues.coupon_id = editData.id;
      initialValues.title = editData.title;
      initialValues.coupon_code = editData.code;
      initialValues.discount_type = editData.discount.isDiscountInPercent ? "PERCENT" : "AMOUNT";
      initialValues.discount = Number(editData.discount.discount);
      initialValues.min_order_amount = Number(editData.minOrderAmount);
      initialValues.max_discount_amount = editData.discount.isDiscountInPercent
        ? Number(editData.discount.maxDiscountAmount)
        : "";
      initialValues.value_share = Number(editData.valueSharePercentage);
      initialValues.status = editData.status === "Active" ? true : false;
      initialValues.start_time = dayjs(editData.validFrom);
      initialValues.end_time = dayjs(editData.validUntil);
      initialValues.promoter_name = editData.promoterName;

      // Relaunch Logic: Reset Status and Dates
      if (isRelaunch) {
        initialValues.start_time = null;
        initialValues.end_time = null;
        initialValues.status = true;
      }

      form.setFieldsValue(initialValues);
    }
  }, [isEdit, editData, isRelaunch, form]);

  // --- VALIDATORS ---
  const validateTitle = (_, value) => {
    const regex = /^[^\u0980-\u09FF\u0600-\u06FF\u4E00-\u9FFF]*$/;
    if (!regex.test(value)) {
      return Promise.reject("Title must contain English characters only");
    }
    if (value.length > 100) {
      return Promise.reject("Title exceeds the maximum character length.");
    }
    return Promise.resolve();
  };

  const validateCode = (_, value) => {
    const regex = /^[\x20-\x7E]*$/;
    if (!regex.test(value)) {
      return Promise.reject("Coupon code must be English alphanumeric characters");
    }
    return Promise.resolve();
  };

  const validateDiscountAmount = ({ getFieldValue }) => ({
    validator(_, value) {
      const type = getFieldValue("discount_type");
      const minOrder = getFieldValue("min_order_amount");
      const maxDisAmount = getFieldValue("max_discount_amount");

      if (type === "AMOUNT") {
        if (minOrder && value > minOrder) {
          return Promise.reject("Discount cannot be greater than Minimum Order Amount.");
        }

        if (value < 0) {
          return Promise.reject("Cannot be negative");
        }
      }
      return Promise.resolve();
    },
  });

  function validateValueShare(_, value) {
    if (value === null || value === undefined || value === "") {
      return Promise.resolve();
    }
    if (value > 100) {
      return Promise.reject(new Error("Cannot exceed 100%"));
    }
    if (value < 0) {
      return Promise.reject(new Error("Cannot be less than 0%"));
    }
    return Promise.resolve();
  }

  function onHandleValueShare(val) {
    if (val === "" || val === null || val === undefined) {
      setValueSharePercentage(null);
      form.setFieldsValue({ value_share: null });
      return;
    }

    const value = Number(val);
    setValueSharePercentage(value);
    form.setFieldsValue({
      value_share: value,
    });
  }

  function onHandleDiscountValue(val) {
    if (val === "" || val === null || val === undefined) {
      setDiscountValue(null);
      form.setFieldsValue({ discount: null });
      return;
    }

    const value = Number(val);
    setDiscountValue(value);
    form.setFieldsValue({
      discount: value,
    });
  }

  function onHandleMinAmount(val) {
    if (val === "" || val === null || val === undefined) {
      setMinOrderAmount(null);
      form.setFieldsValue({ min_order_amount: null });
      return;
    }

    const value = Number(val);
    setMinOrderAmount(value);
    form.setFieldsValue({
      min_order_amount: value,
    });
  }

  function onHandleMaxAmount(val) {
    if (val === "" || val === null || val === undefined) {
      setMaxDiscountAmount(null);
      form.setFieldsValue({ max_discount_amount: null });
      return;
    }

    const value = Number(val);
    setMaxDiscountAmount(value);
    form.setFieldsValue({
      max_discount_amount: value,
    });
  }

  function resetStartTime() {
    setStartDateTime("");
    form.setFieldsValue({
      start_time: dayjs(),
    });
  }

  function onHandleStartDateTime(date, dateString) {
    // const isPastTime = date.format("HH:mm:ss") < dayjs().format("HH:mm:ss");
    if (!date || !dateString) {
      setStartDateTime("");
      form.setFieldsValue({
        start_time: null,
      });
      return;
    }

    const isPastTime = date.isBefore(dayjs());
    if (isPastTime) {
      showNotification("Start Time cannot be a past time.");
      resetStartTime();
      return;
    }

    if (isDate1GreaterThanDate2(dateString, endDateTime)) {
      showNotification("Start Time cannot be greater than End Time.");
      resetStartTime();
      return;
    }

    setStartDateTime(dateString);
    form.setFieldsValue({
      start_time: date,
    });
  }

  function onHandleEndDateTime(date, dateString) {
    if (Boolean(dateString)) {
      const dateObj = dayjs(date);
      const selectedTime = dateObj.format("HH:mm:ss");
      const timeToSet = selectedTime === "00:00:00" ? "23:59:59" : selectedTime;
      const tempToDate = dateObj.format(`YYYY-MM-DD ${timeToSet}`);
      if (startDateTime != "" && !(new Date(tempToDate) >= new Date(startDateTime))) {
        message.warning("This date must be grater than from date.");
        setEndDateTime("");
        return;
      }
      setEndDateTime(tempToDate);
      form.setFieldsValue({
        end_time: dayjs(tempToDate),
      });
    } else {
      setEndDateTime("");
      form.setFieldsValue({
        end_time: null,
      });
    }
  }

  async function couponUpsert(params) {
    setNetworkCall(true);
    try {
      const response = await ApiService.COUPON_UPSERT(params, user.token.token);
      if (response.code !== 200) {
        setNetworkCall(false);
        showNotification(response.data.message);
        return;
      }
      const payload = response.payload;
      const record = payload.record;
      setNetworkCall(false);
      const successMessage = editData ? "Coupon updated successfully!" : "Coupon created successfully!";
      showNotification(successMessage, false);
      return navigate("/coupon-settings");
    } catch (err) {
      showNotification("Something went wrong! Please try again later.");
      setNetworkCall(false);
      return;
    }
  }

  function onHandleCouponUpsert(values) {
    try {
      const payload = {
        couponId: !isRelaunch ? values.coupon_id || "" : "",
        restaurantId: restaurantId,
        code: String(values.coupon_code).toUpperCase(),
        title: values.title,
        validFrom: values.start_time.format("YYYY-MM-DD HH:mm:ss"),
        validUntil: values.end_time.format("YYYY-MM-DD HH:mm:ss"),
        minOrderAmount: values.min_order_amount,
        discountObj: {
          discount: values.discount,
          isDiscountInPercent: values.discount_type === "PERCENT" ? "YES" : "NO",
          maxDiscountAmount: Number(values.max_discount_amount) || 0,
        },
        promoterName: "",
        valueSharePercentage: values.value_share,
        status: values.status ? "Active" : "Inactive",
      };

      couponUpsert(payload);
    } catch (error) {
      message.error("Operation failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white p-1">
      <Form
        form={form}
        layout="vertical"
        onFinish={onHandleCouponUpsert}
        initialValues={{ discount_type: "PERCENT", status: true }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <Form.Item name="coupon_id" hidden>
          <Input />
        </Form.Item>
        {/* Row 1: Title & Code */}
        <Form.Item
          name="title"
          label="Title"
          rules={[
            { required: true, message: "Title is required" },
            { max: 100, message: "Max 100 characters" },
            // { validator: validateTitle },
          ]}
        >
          <Input placeholder="Summer Sale" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="coupon_code"
          label="Coupon Code"
          rules={[
            { required: true, message: "Coupon Code is required" },
            { min: 3, message: "Min 3 chars" },
            { max: 10, message: "Max 10 chars" },
            { validator: validateCode },
          ]}
        >
          {/* Relaunch/Edit logic: If editing, code is often read-only,
              but prompt says "Except Coupon field other fields will be editable"
              only for Relaunch extra conditions. */}
          <Input
            placeholder="SUMMER20"
            disabled={isRelaunch} // Lock code on Edit/Relaunch
            style={{ textTransform: "uppercase" }}
            maxLength={10}
          />
        </Form.Item>

        {/* Row 2: Type & Discount */}
        <Form.Item name="discount_type" label="Discount Type" rules={[{ required: true }]}>
          <Select>
            <Option value="PERCENT">Percent (%)</Option>
            <Option value="AMOUNT">Fixed Amount (TK)</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="discount"
          label="Discount Value"
          dependencies={["min_order_amount", "discount_type"]}
          rules={[
            { required: true, message: "Discount Value is required" },
            discountType === "PERCENT" ? { type: "number", max: 100, message: "Max 100%" } : { type: "number" },
            validateDiscountAmount,
          ]}
        >
          <CustomInputNumber
            value={discountValue}
            setValue={onHandleDiscountValue}
            min={0}
            max={discountType === "PERCENT" ? 100 : 999999}
            placeholder={discountType === "PERCENT" ? "10%" : "50 TK"}
            isDecimalAllowed={true}
          />
        </Form.Item>

        {/* Row 3: Min Order & Max Discount */}
        <Form.Item
          name="min_order_amount"
          label="Minimum Order Amount"
          dependencies={["discount_type"]}
          rules={[
            // If type is AMOUNT (TK), this is Obligatory
            { required: discountType === "AMOUNT", message: "Required for Fixed Amount discount" },
          ]}
        >
          {/*<InputNumber className="w-full" min={0} placeholder="Min Order Amount" />*/}
          <CustomInputNumber
            placeholder="Min Order Amount"
            value={minOrderAmount}
            min={0}
            max={99999}
            setValue={onHandleMinAmount}
            isDecimalAllowed={true}
          />
        </Form.Item>

        <Form.Item
          name="max_discount_amount"
          label="Max Discount Amount"
          dependencies={["discount_type"]}
          rules={
            [
              // Logic: For TK, cannot be greater than Max discount...
              // but prompt says "For TK discount Max discount amount will be disabled"
            ]
          }
        >
          <CustomInputNumber
            placeholder={discountType === "AMOUNT" ? "" : "Max Limit"}
            value={maxDiscountAmount}
            disabled={discountType === "AMOUNT"}
            min={0}
            max={99999}
            setValue={onHandleMaxAmount}
            isDecimalAllowed={true}
          />
        </Form.Item>

        {/* Row 5: Dates */}
        <Form.Item name="start_time" label="Start Time" rules={[{ required: true, message: "Start Time required" }]}>
          <DatePicker
            className={"w-full"}
            showTime={{ format: "HH:mm", showSecond: false }}
            format="YYYY-MM-DD HH:mm"
            onChange={onHandleStartDateTime}
            placeholder="Start time"
            needConfirm
            value={startDateTime ? dayjs(startDateTime) : null}
            disabledDate={(current) => current && current < dayjs().startOf("day")}
          />
        </Form.Item>

        <Form.Item
          name="end_time"
          label="End Time"
          dependencies={["start_time"]}
          rules={[{ required: true, message: "End Time required" }]}
        >
          <DatePicker
            className="w-full"
            showTime={{ format: "HH:mm", showSecond: false }}
            format="YYYY-MM-DD HH:mm"
            onChange={onHandleEndDateTime}
            placeholder="End time"
            needConfirm
            value={endDateTime ? dayjs(endDateTime) : null}
            // disabledDate={(current) => {
            //   if (!startDateTime) return current && current < dayjs(startDateTime).startOf("day");
            //   return current && current < dayjs(startDateTime).startOf("day");
            // }}

            disabledDate={(current) => {
              // If startDateTime exists, use it. Otherwise, default to the current day.
              const baseDate = startDateTime ? dayjs(startDateTime) : dayjs();

              // Disable any date that comes before the start of the baseDate
              return current && current < baseDate.startOf("day");
            }}
          />
        </Form.Item>

        {/* Row 4: Value Share & Status */}
        <Form.Item
          name="value_share"
          label="Value Share (%)"
          rules={[
            { required: true, message: "Value Share (%) is required" },
            { type: "number", max: 100, message: "Cannot exceed 100%" },
            { validator: validateValueShare },
          ]}
        >
          {/* <InputNumber className="w-full" min={0} max={100} /> */}
          <CustomInputNumber
            value={valueSharePercentage}
            setValue={onHandleValueShare}
            min={0}
            max={100}
            isDecimalAllowed={true}
          />
        </Form.Item>
        <ConfigProvider
          theme={{
            components: {
              Switch: {
                trackHeight: 32, // Matches standard Input height
                handleSize: 28, // Proportional toggle circle (32 - 4)
              },
            },
          }}
        >
          <Form.Item name="status" label="Status" valuePropName="checked">
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              style={{ minWidth: "90px" }} // Ensures the text fits nicely inside the taller button
            />
          </Form.Item>
        </ConfigProvider>

        {/* Actions */}
        <div className="md:col-span-2 flex justify-end gap-3 mt-0">
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: "#FA6060",
                colorPrimaryHover: "#FA6060",
                colorPrimaryActive: "#FA6060",
                colorTextLightSolid: "#fff",
              },
            }}
          >
            <Button className="font-ubold h-[42px]" type="primary" onClick={() => navigate("/coupon-settings")}>
              Cancel
            </Button>
          </ConfigProvider>

          <ConfigProvider
            theme={{
              token: {
                colorPrimary: "#2EC114",
                colorPrimaryHover: "#2EC114",
                colorPrimaryActive: "#2EC114",
                colorTextLightSolid: "#fff",
              },
            }}
          >
            <Button className="font-ubold h-[42px]" type="primary" htmlType="submit" loading={loading}>
              {!isRelaunch ? (Object.keys(editData).length > 0 ? "Update Coupon" : "Create Coupon") : "Relaunch Coupon"}
            </Button>
          </ConfigProvider>
        </div>
      </Form>
    </div>
  );
}
